import { useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { api } from "@shared/routes";

type EventHandler = (data: any) => void;

const eventHandlers = new Map<string, Set<EventHandler>>();
let globalEventSource: EventSource | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let subscriberCount = 0;

function connectSSE(queryClient: ReturnType<typeof useQueryClient>) {
  if (globalEventSource) return;

  const es = new EventSource("/api/events", { withCredentials: true });
  globalEventSource = es;

  es.addEventListener("notification", (e) => {
    const data = JSON.parse(e.data);
    queryClient.invalidateQueries({ queryKey: [api.notifications.list.path] });
    queryClient.invalidateQueries({ queryKey: [api.notifications.unreadCount.path] });
    const handlers = eventHandlers.get("notification");
    if (handlers) handlers.forEach((h) => h(data));
  });

  es.addEventListener("reservation_created", (e) => {
    const data = JSON.parse(e.data);
    queryClient.invalidateQueries({ queryKey: ["/api/admin/reservations"] });
    queryClient.invalidateQueries({ queryKey: ["/api/admin/dashboard"] });
    const handlers = eventHandlers.get("reservation_created");
    if (handlers) handlers.forEach((h) => h(data));
  });

  es.addEventListener("reservation_updated", (e) => {
    const data = JSON.parse(e.data);
    queryClient.invalidateQueries({ queryKey: ["/api/admin/reservations"] });
    queryClient.invalidateQueries({ queryKey: ["/api/admin/dashboard"] });
    const handlers = eventHandlers.get("reservation_updated");
    if (handlers) handlers.forEach((h) => h(data));
  });

  es.addEventListener("cleaning_updated", (e) => {
    const data = JSON.parse(e.data);
    queryClient.invalidateQueries({ queryKey: ["/api/admin/cleaning-jobs"] });
    queryClient.invalidateQueries({ queryKey: ["/api/cleaner/jobs"] });
    const handlers = eventHandlers.get("cleaning_updated");
    if (handlers) handlers.forEach((h) => h(data));
  });

  es.addEventListener("ticket_updated", (e) => {
    const data = JSON.parse(e.data);
    queryClient.invalidateQueries({ queryKey: ["/api/admin/tickets"] });
    const handlers = eventHandlers.get("ticket_updated");
    if (handlers) handlers.forEach((h) => h(data));
  });

  es.addEventListener("crm_message", (e) => {
    const data = JSON.parse(e.data);
    queryClient.invalidateQueries({ queryKey: ["/api/admin/crm/conversations"] });
    const handlers = eventHandlers.get("crm_message");
    if (handlers) handlers.forEach((h) => h(data));
  });

  es.addEventListener("qa_zone_scanning", (e) => {
    const data = JSON.parse(e.data);
    if (data.inspectionId) {
      queryClient.invalidateQueries({ queryKey: ["/api/qa/inspection", data.jobId] });
    }
    const handlers = eventHandlers.get("qa_zone_scanning");
    if (handlers) handlers.forEach((h) => h(data));
  });

  es.addEventListener("qa_zone_result", (e) => {
    const data = JSON.parse(e.data);
    if (data.jobId) {
      queryClient.invalidateQueries({ queryKey: ["/api/qa/inspection", data.jobId] });
    }
    const handlers = eventHandlers.get("qa_zone_result");
    if (handlers) handlers.forEach((h) => h(data));
  });

  es.addEventListener("qa_inspection_complete", (e) => {
    const data = JSON.parse(e.data);
    queryClient.invalidateQueries({ queryKey: ["/api/admin/cleaning-jobs"] });
    queryClient.invalidateQueries({ queryKey: ["/api/cleaner/jobs"] });
    if (data.jobId) {
      queryClient.invalidateQueries({ queryKey: ["/api/qa/inspection", data.jobId] });
    }
    const handlers = eventHandlers.get("qa_inspection_complete");
    if (handlers) handlers.forEach((h) => h(data));
  });

  es.onerror = () => {
    es.close();
    globalEventSource = null;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = setTimeout(() => {
      if (subscriberCount > 0) connectSSE(queryClient);
    }, 5000);
  };
}

function disconnectSSE() {
  if (globalEventSource) {
    globalEventSource.close();
    globalEventSource = null;
  }
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

export function useRealtimeEvents(eventType?: string, handler?: EventHandler) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!user) return;

    subscriberCount++;
    connectSSE(queryClient);

    return () => {
      subscriberCount--;
      if (subscriberCount <= 0) {
        subscriberCount = 0;
        disconnectSSE();
      }
    };
  }, [user, queryClient]);

  useEffect(() => {
    if (!eventType || !handler) return;

    const wrappedHandler: EventHandler = (data) => {
      handlerRef.current?.(data);
    };

    if (!eventHandlers.has(eventType)) {
      eventHandlers.set(eventType, new Set());
    }
    eventHandlers.get(eventType)!.add(wrappedHandler);

    return () => {
      eventHandlers.get(eventType)?.delete(wrappedHandler);
    };
  }, [eventType]);
}
